'use strict';

import { Buildings, isMobile, projectMode, settingsData, threeJsVariables } from '../../settings';

import {
    Mesh,
    PlaneBufferGeometry,
    ShaderMaterial,
    Vector2,
    RepeatWrapping,
    TOUCH,
    Group,
    Vector3,
    OrthographicCamera,
    MOUSE, MeshStandardMaterial, Raycaster,
} from 'three';
import liteModeVertex from '../threejs/Shaders/liteMode/liteModeVertex.glsl';
import liteModeFragment from '../threejs/Shaders/liteMode/liteModeFragment.glsl';
import * as dat from 'dat.gui';
import { compassVisibleToggle } from '../compass/compass';
import { getCanvasWidthDiff } from '../../app';
import { getCurrentLangDirection } from '../language/langSwitcher';
import {
    createOrbitToMapButton,
    hideOrbitToMapButton,
    setMinAndMaxControlsDistance,
    showOrbitToMapButton
} from '../controls/orbitControls';
import gsap from 'gsap';
import {
    ANIMATION_INTRO,
    flyCameraAnimation,
    MODE_ANIMATION,
    OrbitToMap,
    setCameraMode,
} from '../threejs/camera';
import {FBXLoader} from "three/examples/jsm/loaders/FBXLoader";
import {getCrmData, getPoiData} from "../crm/crm-requests";
import {
    createBuildingInfo, setDataToAmenities,
    setDataToApartment,
    setProjectSettings
} from "../threejs/autoSelectionBoxes";
import {
    createHtmlElement,
    createMaterialAndSetBaseColor,
    getUrlParam,
    lazyLoadingData,
} from '../../functions';
import { Building, BuildingWithoutModel } from '../building/Building';
import {ViewMode} from "../../common";
import {createTooltip} from "../flatTooltip/flatTooltip";
import {OrbitControls} from '../../libs/OrbitControls';


export class LiteMode {
    #renderSizeVec = new Vector2();
    #pointDown = new Vector2();
    #pointUp = new Vector2();
    #maxPan = new Vector3();
    #minPan = new Vector3();
    #bounceZoom = 0;
    #tryCount = 0;
    #increaseBtn = null;
    #decreaseBtn = null;

    constructor () {
        this.active = config.features.liteMode?.active;
        this.material = null;
        this.overlay = null;
        this.imageWidth = 0;
        this.imageHeight = 0;
        this.aspect = new Vector2(1.0, 1.0);
        this.camera =  new OrthographicCamera(-1, 1, 1, -1, 0, 1);
        this.raycaster = new Raycaster();
        this.intersections = [];
        this.intersectedObject = null;
        this.controls = new OrbitControls(this.camera,threeJsVariables.renderer.domElement);
        this.models = [];
        this.blockPointerEvents = false;
        this.settings = {};
        this.currentZoom = 1.0;
        this.prevZoom = 1;
        this.canvas = document.querySelector('#c');
        this.blockEvents = false;
        this.debugActive = getUrlParam('lite-mode-debug');
        this.resourcesUrl = `${config.cdnRepository}/${config.projectFolder}/resources/litemod/`;
        this.mode = 'picture';
        this.defaultSelectionScale = 0;

        switch (config.features.liteMode?.mode) {
            case 'regular':
                this.mode = 'regular';
                break;
            case 'schematic':
                this.mode = 'schematic';
                break;
        }
    }

    async init (textureLoader, manager) {
        if (!this.isActive()) return;

        const { images, settings, mapping,models } = config.features.liteMode;
        this.settings = settings;
        this.models = models;

        this.controls.screenSpacePanning = true;
        this.controls.minZoom = 1;
        this.controls.maxZoom = settings.maxZoom;
        this.controls.mouseButtons = {
            LEFT: MOUSE.PAN,
            MIDDLE: MOUSE.DOLLY
        };

        this.controls.touches = {
            ONE: TOUCH.PAN,
            TWO: TOUCH.DOLLY_PAN
        };

        this.controls.enableDamping = settings.damping.active;
        this.controls.dampingFactor = settings.damping.dampingFactor;
        this.controls.zoomSpeed = settings.zoomStep;

        this.controls.maxPan = this.#maxPan;
        this.controls.minPan = this.#minPan;
        this.controls.object.zoom = settings.initialZoom;
        this.controls.object.updateProjectionMatrix();
        this.#controlsHandler();

        ['pointerup', 'pointerdown', 'pointermove'].forEach(itemEvent =>{
            this.canvas.addEventListener(itemEvent, this.#pointerHandler.bind(this));
        });

        this.controls.addEventListener('change', this.#controlsHandler.bind(this));

        this.preventPointerEvents(true);

        // Load selections
        const fbxLoader = new FBXLoader();
        const selections = await fbxLoader.loadAsync(this.resourcesUrl + mapping);
        const selectionsGroup = new Group();

        selections.traverse(el => {
           if (el.isMesh) {

               const newMaterial = new MeshStandardMaterial({
                   depthTest: false,
                   depthWrite: false,
                   transparent: true,
                   opacity: 0
               });

               const geom = el.geometry.clone();
               const mesh = new Mesh(geom,newMaterial);
               mesh.name = el.name;
               mesh.scale.copy(el.scale);
               this.defaultSelectionScale = el.scale.x;

               selectionsGroup.add(mesh);
               this.intersections.push(mesh);
           }
        });

        const generalImage = await textureLoader.loadAsync(this.resourcesUrl + images.general);
        const skyTexture = await textureLoader.loadAsync(this.resourcesUrl + images.sky);

        let depthImage = null;

        if (images.depth){
            depthImage = await textureLoader.loadAsync(this.resourcesUrl + images.depth);
        }

        skyTexture.wrapS = RepeatWrapping;
        skyTexture.wrapT = RepeatWrapping;

        this.imageWidth = generalImage.image.width;
        this.imageHeight = generalImage.image.height;

        const geometry = new PlaneBufferGeometry(2, 2, 1, 1);

        this.material = new ShaderMaterial({
            vertexShader: liteModeVertex,
            transparent: true,
            fragmentShader: liteModeFragment,
            uniforms: {
                generalTexture: {
                    value: generalImage,
                },
                depthTexture: {
                    value: depthImage,
                },
                skyTexture: {
                    value: skyTexture,
                },
                orbitToMapProgress: {
                    value: 0.0
                },
                skyActive: {
                  value: this.settings.sky.active
                },
                birdsActive: {
                  value: this.settings.birds.active
                },
                skySpeed: {
                    value: this.settings.sky.skySpeed,
                },
                hoverTexture: {
                    value: null,
                },
                uCircleScale: {
                    value: 0.0
                },
                progress: {
                    value: 0.0
                },
                uDepthMouse: {
                    value: new Vector2(0, 0),
                },
                uAspect: {
                    value: this.aspect,
                },
                uTime: {
                    value: 0.0,
                },
            },
        });

        if (this.debugActive){
            this.#debugMode();
        }

        this.updateAspect();

        this.overlay = new Mesh(geometry, this.material);
        this.overlay.name = 'Lite mode';
        this.overlay.add(selectionsGroup);

        threeJsVariables.scene.add(this.overlay);

        this.loadBuildingModel(manager);
    }

    #pointerHandler(event) {
        if (!this.isVisible() || this.blockEvents) return;

        threeJsVariables.renderer.getSize(this.#renderSizeVec);
        const canvasDiff = getCanvasWidthDiff();

        threeJsVariables.mouse.x = ((event.clientX - canvasDiff.x) / this.#renderSizeVec.x) * 2 - 1;
        threeJsVariables.mouse.y = -(event.clientY / this.#renderSizeVec.y) * 2 + 1;

        // Raycasting selections
        this.raycaster.setFromCamera(threeJsVariables.mouse,this.camera);
        const intersects = this.raycaster.intersectObjects(this.intersections,false);

        if (intersects.length) {
            this.canvas.style.cursor = 'pointer';
            this.intersectedObject = intersects[0].object;
            this.intersectedObject.material.emissive.setHex(0xffffff);
            this.intersectedObject.material.opacity = 0.2;
        } else {
            this.canvas.style.cursor = '';

            if (this.intersectedObject) {
                this.intersectedObject.material.opacity = 0;
                this.intersectedObject = null;
            }
        }

        switch (event.type) {
            case 'pointermove':
                break;
            case 'pointerdown':
                this.#pointDown.copy(threeJsVariables.mouse);
                break;
            case 'pointerup':
                this.#pointUp.copy(threeJsVariables.mouse);
                this.#clickListener();
                break;
        }
    }

    #controlsHandler() {
        if (this.blockEvents) return;

        this.currentZoom = this.camera.zoom;

        if (this.currentZoom < this.prevZoom) {
            // Zoom out
            const vMin = new Vector3(-(this.currentZoom - 1) / this.currentZoom ,-(this.currentZoom - 1) / this.currentZoom ,0);
            const vMax = new Vector3((this.currentZoom - 1) / this.currentZoom ,(this.currentZoom - 1) / this.currentZoom ,0);

            this.controls.target.clamp(vMin,vMax);
            this.controls.object.position.clamp(vMin,vMax);
        }

        this.#maxPan.set((this.currentZoom - 1) / this.currentZoom ,(this.currentZoom - 1) / this.currentZoom ,0);
        this.#minPan.set(-(this.currentZoom - 1) / this.currentZoom ,-(this.currentZoom - 1) / this.currentZoom ,0);


        // this.#resizeHoverPoints();
        this.zoomListener();
        this.prevZoom = this.currentZoom;
    }

    async loadBuildingModel(manager) {

        if (this.mode === 'picture'){
            const [crmSettings, crmProps] = await getCrmData();
            console.log(crmSettings,crmProps);
            setProjectSettings(crmSettings, config.dictionary);
            settingsData.allCrmData = crmProps.data;

            const allBuildings = crmSettings.data.buildings;

            allBuildings.forEach(building => {
               const buildingObject = new BuildingWithoutModel(building.buildingEngineId,crmProps.data,crmSettings);
               Buildings.addBuilding(building.buildingEngineId,buildingObject);
            });

            const poiData = getPoiData();
            poiData.then(response => {
                config.poiData = response[0].data;
                config.poiCategories = response[1].data;
            });

            flyCameraAnimation();

            if (!isMobile) createTooltip();

            setMinAndMaxControlsDistance(ViewMode.Orbit);
            setCameraMode(MODE_ANIMATION,ANIMATION_INTRO);


        } else if (this.mode === 'schematic'){
            const loader = new FBXLoader(manager);
            const crmData = getCrmData();
            const projectModels = config.features.liteMode.models;
            const selectionBoxesModels = {};
            const selectionBoxesCrmData = {};
            const selections = [];
            const floorsPlug = [];
            const projectGroup = new Group();
            projectGroup.name = 'Project Buildings';

            let crmSettings = null;
            let crmProps = null;
            let dictionary = null;

            for (let i = 0; i < projectModels.length; i++) {
                const url = this.resourcesUrl + projectModels[i].building + '_selectBoxes.fbx';
                const currentItem = await lazyLoadingData(loader, url);
                projectGroup.add(currentItem);
                currentItem.name = currentItem.name.split('_')[0];
                selectionBoxesModels[currentItem.name.split('_')[0]] = currentItem;
            }

            await crmData.then(response => {
                [crmSettings, crmProps, dictionary] = response;
                setProjectSettings(crmSettings, dictionary);
                settingsData.allCrmData = crmProps.data;

                response.forEach((element => {
                    if (Array.isArray(element.data))
                        selectionBoxesCrmData[element.data[0].buildingEngineId] = element.data;
                }));

                for (let key in selectionBoxesModels) {

                    new Building(selectionBoxesModels[key]).create();
                    const currentBuilding = Buildings.getBuilding(key);

                    selectionBoxesModels[key].traverse((element) => {

                        const existProp = selectionBoxesCrmData[key].find(prop => prop.engineId === element.name);
                        if (existProp) {
                            setDataToApartment(element, existProp);
                            createMaterialAndSetBaseColor(element);
                            currentBuilding.apartments.push(element);
                        }

                        if (element.name.match(/_amenity_/g)) {
                            createMaterialAndSetBaseColor(element);
                            setDataToAmenities(element);
                            currentBuilding.customSelections.push(element);
                        }
                    });

                    selectionBoxesModels[key].children.forEach((child) => {
                        const currentFloor = parseInt(child.name.split('_')[1]);
                        child.userData.basePosition = new Vector3(child.position.x, child.position.y, child.position.z);
                        child.userData.floor = currentFloor;
                        selections.push({floor: currentFloor, data: child.children});
                        child.traverse((element) => {
                            if (element.isMesh && element.name.split('_')[0] === 'zero') {
                                floorsPlug.push({floor: currentFloor, data: element})
                                element.userData.floor = currentFloor;
                            }
                        });
                    });

                    currentBuilding.selections = selections;
                    currentBuilding.floorsPlug = floorsPlug;
                    currentBuilding.selectionsGroup = selectionBoxesModels[key];
                    currentBuilding.model = null;
                    currentBuilding.instances = null;
                    currentBuilding.info = createBuildingInfo(selectionBoxesModels[key], crmProps.data, crmSettings);
                }

                const poiData = getPoiData();
                poiData.then(response => {
                    config.poiData = response[0].data;
                    config.poiCategories = response[1].data;
                });

                flyCameraAnimation();

            });

            if (!isMobile) createTooltip();

            threeJsVariables.scene.add(projectGroup);
            setMinAndMaxControlsDistance(ViewMode.Orbit);
            setCameraMode(MODE_ANIMATION,ANIMATION_INTRO);
        }

    }

    renderTargetLayer() {
        if (this.isActive() && this.isVisible()){
            threeJsVariables.renderer.autoClear = false;
            threeJsVariables.renderer.render(this.overlay, this.camera);
        }
    }

    #debugMode() {
        let setPoint = false;
        let tempPoints = [];
        const maps = [];

        const debugPanel = createHtmlElement(document.body,'','div',['lite-mode__debug-panel']);
        const pointBtn = createHtmlElement(debugPanel,'Add highlight map','button',['lite-mode__debug-panel__btn']);
        const exportPoints = createHtmlElement(debugPanel,'Export highlights','button',['lite-mode__debug-panel__btn']);
        const mapList = createHtmlElement(debugPanel,'','ul',['lite-mode__debug-panel__list']);

        exportPoints.addEventListener('click', () => {
            prompt('Copy and paste to config',JSON.stringify(maps));
        });
        pointBtn.addEventListener('click', addHighlightMapHandler);
        document.body.addEventListener('keydown', activatePointHandler);
        document.body.addEventListener('keyup', activatePointHandler);
        this.canvas.addEventListener('click', setPointHandler);
        this.canvas.addEventListener('mousemove', (event) => {
            threeJsVariables.renderer.getSize(this.#renderSizeVec);
            const canvasDiff = getCanvasWidthDiff();
            const langDir = getCurrentLangDirection();
            if (langDir === 'ltr') {
                canvasDiff.x = 0;
            }

            threeJsVariables.mouse.x = ((event.clientX - canvasDiff.x) / this.#renderSizeVec.x) * 2 - 1;
            threeJsVariables.mouse.y = -(event.clientY / this.#renderSizeVec.y) * 2 + 1;
        });

        this.blockEvents = true;

        // GUI
        const options = {
            zoom: 1.0,
            center: {
                x: 0.0,
                y: 0.0,
            },
            camera: {
                azimuth: 0.0
            },
            circle: {
                scale: 0.0
            },
            pr: {
                progress: 0.0
            },
            orbToMap: {
                orbitProgress: 0.0
            }

        };

        const gui = new dat.GUI();

        gui.add(options.circle, 'scale', 0.0, 4.0, 0.001).onChange((scale) => {
            this.material.uniforms.uCircleScale.value = scale;
        });

        gui.add(options.pr, 'progress', 0.0, 4.0, 0.001).onChange((progress) => {
            this.material.uniforms.progress.value = progress;
        });

        gui.add(options.orbToMap, 'orbitProgress', 0.0, 4.0, 0.001).onChange((orbitProgress) => {
            this.material.uniforms.orbitToMapProgress.value = orbitProgress;
        });

        /// DEBUG MODE FUNCTIONS
        function addHighlightMapHandler () {
            const mapItem = createHtmlElement(mapList,'','li',['lite-mode__debug-panel__list-item']);
            const buildingName = createHtmlElement(mapItem,'','input',['lite-mode__debug-panel__list-item__name']);
            buildingName.setAttribute('placeholder','Building name');
            const points = createHtmlElement(mapItem,'','input',['lite-mode__debug-panel__list-item__points']);
            points.setAttribute('disabled', true);
            const btnsContainer = createHtmlElement(mapItem,'','div',['lite-mode__debug-panel__list-item__buttons-container']);
            const saveBtn = createHtmlElement(btnsContainer,'Save','button',['lite-mode__debug-panel__list-item__btn']);
            const deleteBtn = createHtmlElement(btnsContainer,'Delete','button',['lite-mode__debug-panel__list-item__btn','lite-mode__debug-panel__list-item__delete-btn']);

            mapItem.addEventListener('click', () => {
                const activeItem = mapList.querySelector('.lite-mode__debug-panel__list-item.active');
                if (activeItem) activeItem.classList.remove('active');
                if (mapItem.classList.contains('saved')) return;
                mapItem.classList.add('active');
            });

            deleteBtn.addEventListener('click', () => {
               mapItem.remove();
               tempPoints = [];
            });

            saveBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                if (buildingName.value.trim().length === 0 || points.value.length === 0) return;

                const map = {
                    building: buildingName.value,
                    hover: buildingName.value + '.jpg',
                    coords: tempPoints
                };
                maps.push(map);
                tempPoints = [];
                saveBtn.style.display = 'none';
                deleteBtn.style.display = 'none';
                buildingName.setAttribute('disabled', true);
                mapItem.classList.remove('active');
                mapItem.classList.add('saved');
                createHtmlElement(btnsContainer,'Saved','p',[]);
            });

        }

        function activatePointHandler (e) {
            switch (e.type) {
                case 'keydown':
                        setPoint = true;
                    break;
                case 'keyup':
                        setPoint = false;
                    break;
            }
        }

        function setPointHandler (e) {
            if (setPoint) {
                const point = [threeJsVariables.mouse.x,threeJsVariables.mouse.y];
                const activeMapPoints = mapList.querySelector('.lite-mode__debug-panel__list-item.active');

                if (activeMapPoints){
                    const pointsField = activeMapPoints.querySelector('.lite-mode__debug-panel__list-item__points');
                    pointsField.value += `[${point}]/`;
                    tempPoints.push(point);
                }
            }

        }

    }

    #clickListener(){

        if (this.blockEvents) return;

        if (this.#pointDown.x.toFixed(2) === this.#pointUp.x.toFixed(2) && this.#pointDown.y.toFixed(2) === this.#pointUp.y.toFixed(2)) {

            if (this.intersectedObject) {
                this.intersectedObject.material.opacity = 0;
                const building = Buildings.getBuilding(this.intersectedObject.name);
                this.intersectedObject = null;
                projectMode.Cylinder.enter(building);
            }
        }
    }

    zoomListener(target){
        if (!this.isVisible() || this.blockEvents) return;

        this.#decreaseBtn = document.querySelector('.zoom__controls__btn[data-zoom-btn="decrease"]');
        this.#increaseBtn = document.querySelector('.zoom__controls__btn[data-zoom-btn="increase"]');

        if (target){
            switch (target.dataset.zoomBtn) {
                case 'decrease':
                    !this.#decreaseBtn?.classList.contains('disabled') && this.controls.dollyOutCamera();
                    break;
                case 'increase':
                    !this.#increaseBtn?.classList.contains('disabled') && this.controls.dollyInCamera();
                    break;
            }
        }

        if (this.currentZoom === this.controls.minZoom) {
            this.#decreaseBtn?.classList.add('disabled');

            if (this.#tryCount > 1){
                OrbitToMap();
                this.#decreaseBtn?.classList.remove('disabled');
                this.#tryCount = 0;
                return;
            }

            showOrbitToMapButton();

            this.#borderBounce();

        } else if (this.currentZoom > this.#bounceZoom && this.currentZoom < this.controls.maxZoom) {
            hideOrbitToMapButton();
            this.#increaseBtn?.classList.remove('disabled');
            this.#tryCount = 0;
            this.blockEvents = false;
        } else if (this.currentZoom === this.controls.maxZoom){
            this.#increaseBtn?.classList.add('disabled');
            this.blockEvents = false;
        }
    }

    #borderBounce(){
        if (this.blockEvents) return;
        this.blockEvents = true;

        const bounce = { value: this.currentZoom};

        gsap.to(bounce,{
            value: this.currentZoom * 1.1,
            duration: 0.2,
            onStart: () => {
                this.#bounceZoom = this.currentZoom * 1.1;
            },
            onUpdate: () => {
                this.currentZoom = bounce.value;
                this.camera.zoom = this.currentZoom;
                this.camera.updateProjectionMatrix();
            },
            onComplete: () => {
                this.blockEvents = false;
                this.#decreaseBtn?.classList.remove('disabled');
                this.#tryCount ++;
            }
        });
    }

    runProjectStartAdditionalFunctions(){
        if (!this.isActive()) return;

        compassVisibleToggle('hide');
        createOrbitToMapButton();

        threeJsVariables.orbitControls.enabled = false;
        threeJsVariables.trackballControls.enabled = false;

    }

    updateAspect () {
        if (!this.isActive() || this.debugActive) return;
        threeJsVariables.renderer.getSize(this.#renderSizeVec);
        const imageAspect = this.imageWidth / this.imageHeight;
        const renderAspect = this.#renderSizeVec.x / this.#renderSizeVec.y;
        // const renderAspect = window.innerWidth / window.innerHeight;

        if (renderAspect < imageAspect) {
            this.aspect.set(renderAspect / imageAspect, 1.0);
        } else {
            this.aspect.set(1.0, imageAspect / renderAspect);
        }

        // Resize selections
        this.intersections.forEach(el => {
            let scale = {x: this.defaultSelectionScale, y: this.defaultSelectionScale, z: this.defaultSelectionScale};

            if (this.aspect.x < 1){
                scale.x = this.defaultSelectionScale / this.aspect.x;
            }

            if (this.aspect.y < 1) {
                scale.y = this.defaultSelectionScale / this.aspect.y;
            }

            el.scale.set(scale.x,scale.y,scale.z);

        });
    }

    preventPointerEvents(action = undefined){
        if (!this.isActive()) return false;

        if (action !== undefined){
            this.blockPointerEvents = action;
        } else {
            return this.blockPointerEvents;
        }
    }

    controlsToggle(enable){
        if (!this.isActive()) return;
        threeJsVariables.orbitControls.enabled = enable;
        threeJsVariables.trackballControls.enabled = enable;
    }

    resizeContainer () {
        if (!this.isActive() || !this.isVisible()) return;
        this.updateAspect();
        this.material.uniforms.uAspect.value = this.aspect;
    }

    updateTime (delta) {
        if (!this.isActive() || !this.isVisible()) return;
        this.controls.update();
        this.material.uniforms.uTime.value += delta;
    }

    showLiteMode () {
        if (this.blockEvents) return;
        this.blockEvents = true;

        const scale = { value: this.material.uniforms.uCircleScale.value};

        gsap.to(scale,{
            value: 0,
            duration: 1.0,
            onUpdate: () => {
                this.material.uniforms.uCircleScale.value = scale.value;
            },
            onStart: () => {
                this.#layerVisibility(true);
                this.updateAspect();
                this.preventPointerEvents(true);
                threeJsVariables.orbitControls.enabled = false;
                threeJsVariables.trackballControls.enabled = false;
            },
            onComplete: () => {
                this.blockEvents = false;
            }
        })
    }

    #layerVisibility(state){
        this.overlay.visible = state;
    }

    hideLiteMode () {
        if (this.blockEvents || this.mode === 'picture') return;
        this.blockEvents = true;

        const scale = { value: 0};

        gsap.to(scale,{
            value: 1.5,
            duration: 2.0,
            onUpdate: () => {
                this.material.uniforms.uCircleScale.value = scale.value;
            },
            onComplete: () => {
                this.#layerVisibility(false);
                this.preventPointerEvents(false);
                threeJsVariables.orbitControls.enabled = true;
                threeJsVariables.trackballControls.enabled = true;
                this.blockEvents = false;
            }
        })
    }

    mapAnimation(state){
        if (this.blockEvents) return;
        this.blockEvents = true;

        const progress = { start: 0, end: 0 };

        switch (state) {
            case 'toMap':
                progress.end = 1.3;
                break;
            case 'toOrbit':
                progress.start = 1.3;
                progress.end = 0.0;
                break;

        }

        gsap.to(progress,{
            start: progress.end,
            duration: 2.0,
            onUpdate: () => {
                this.material.uniforms.orbitToMapProgress.value = progress.start;
            },
            onComplete: () => {
                this.material.uniforms.orbitToMapProgress.value = 0;
                this.currentZoom = this.settings.initialZoom;
                this.camera.zoom = this.currentZoom;
                this.blockEvents = false;
                this.camera.updateProjectionMatrix();
                this.#controlsHandler();
            },
            onStart: () => {
                hideOrbitToMapButton();
                this.#decreaseBtn?.classList.remove('disabled');
                this.#increaseBtn?.classList.remove('disabled');
            }
        });
    }

    moveCameraToBuilding(name) {
    //     console.log('move camera to building');
    //     return;
    //     const start = {
    //         zoom: this.currentZoom,
    //         x: this.material.uniforms.uMouse.value.x,
    //         y: this.material.uniforms.uMouse.value.y
    //     };
    //
    //     const buildingObject = this.maps.find(el => el.building === name);
    //     const buildingCoords = buildingObject.coords[0];
    //
    //     gsap.to(start,{
    //         zoom: this.settings.maxZoom,
    //         x: buildingCoords[0],
    //         y: buildingCoords[1],
    //         duration: 1,
    //         onUpdate: () => {
    //             this.currentZoom = start.zoom;
    //             this.material.uniforms.zoom.value = this.currentZoom;
    //
    //             this.#dragMousePosition.set(start.x,start.y);
    //
    //             this.#dragMousePosition.clamp(
    //                 this.#dragV1.set(-((this.currentZoom - 1) / this.currentZoom) ,-((this.currentZoom - 1) / this.currentZoom)),
    //                 this.#dragV2.set((this.currentZoom - 1) / this.currentZoom, (this.currentZoom - 1) / this.currentZoom)
    //             );
    //
    //             this.material.uniforms.uMouse.value.copy(this.#dragMousePosition);
    //         },
    //         onStart: () => {
    //             this.blockHover = true;
    //             this.material.uniforms.hoverTexture.value = null;
    //         },
    //         onComplete: () => {
    //             this.#resizeHoverPoints();
    //             this.material.uniforms.hoverTexture.value = this.hoverTextures[buildingObject.building];
    //         }
    //     });
    //
    }
    //
    moveCameraFromBuilding() {
    //     console.log('move camera from building');
    //     return;
    //     const start = {
    //         zoom: this.currentZoom,
    //         x: this.material.uniforms.uMouse.value.x,
    //         y: this.material.uniforms.uMouse.value.y
    //     };
    //
    //     gsap.to(start, {
    //         zoom: this.settings.initialZoom,
    //         x: 0,
    //         y: 0,
    //         duration: 0.5,
    //         onUpdate: () => {
    //             this.currentZoom = start.zoom;
    //             this.material.uniforms.zoom.value = this.currentZoom;
    //
    //             this.#dragMousePosition.set(start.x,start.y);
    //             //
    //             this.#dragMousePosition.clamp(
    //                 this.#dragV1.set(-((this.currentZoom - 1) / this.currentZoom) ,-((this.currentZoom - 1) / this.currentZoom)),
    //                 this.#dragV2.set((this.currentZoom - 1) / this.currentZoom, (this.currentZoom - 1) / this.currentZoom)
    //             );
    //
    //             this.material.uniforms.uMouse.value.copy(this.#dragMousePosition);
    //         },
    //         onComplete: () => {
    //             this.#resizeHoverPoints();
    //             this.material.uniforms.hoverTexture.value = null;
    //             this.blockHover = false;
    //         }
    //     })
    }

    isVisible(){
        return this.overlay?.visible;
    }

    isActive () {
        return this.active;
    }

    modeIs (name) {
        if (!this.active) return false;

        return this.mode === name;
    }

}
